/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { createPath } from 'history';
import { di } from 'react-magnetic-di';
import type { BrowserHistory, DynamicRouteBrowserHistory, Location } from '../../types.tsx';

type HistoryAction = 'POP' | 'PUSH' | 'REPLACE';
type Listener = (location: Location, action: HistoryAction) => void;

const hasWindow = () => typeof window !== 'undefined';

const methodsPlaceholders = {
	push: noop,
	replace: noop,
	goBack: noop,
	goForward: noop,
	listen: () => noop,
	block: () => noop,
	refreshRoutes: () => noop,
};

const getLocation = () => {
	di(window);
	// todo - don't force non-optional search and hash

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const { pathname = '', search = '', hash = '' } = (hasWindow() && window.location) || {};

	return { pathname, search, hash };
};

const createLegacyListener = (updateExposedLocation: Listener) => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const node = document.querySelector('#content');

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	let prevHref = window.location.href;
	let listeners: Listener[] = [];
	const historyStack = [prevHref];
	// note: window.history.length is capped at 50 entries

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	let prevHistoryLength = window.history.length;
	let historyIndex = 0;
	const getAction = (): HistoryAction => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const currentHref = window.location.href;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (prevHistoryLength === window.history.length) {
			if (historyStack[historyIndex - 1] === currentHref) {
				historyIndex -= 1;

				return 'POP';
			}
			if (historyStack[historyIndex + 1] === currentHref) {
				historyIndex += 1;

				return 'POP';
			}
			historyStack[historyStack.length - 1] = currentHref;

			return 'REPLACE';
		}
		historyStack.push(currentHref);
		historyIndex += 1;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		prevHistoryLength = window.history.length;

		return 'PUSH';
	};
	const onNodeChanges = debounce(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (prevHref !== window.location.href) {
			const newLocation = getLocation();
			const action = getAction();
			updateExposedLocation(newLocation, action);

			listeners.forEach((listener) => listener(newLocation, action));

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			prevHref = window.location.href;
		}
	}, 50);

	if (node) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const observer = new window.MutationObserver(onNodeChanges);
		observer.observe(node, {
			childList: true,
			subtree: true,
			attributes: true,
			characterData: true,
		});
	}

	return (listener: any) => {
		listeners = listeners.concat(listener);

		return () => {
			listeners = listeners.filter((lst) => lst !== listener);
		};
	};
};

export const createLegacyHistory = (): BrowserHistory & DynamicRouteBrowserHistory => {
	di(window);
	let currentLocation = getLocation();
	let currentAction: HistoryAction = 'PUSH';
	const updateExposedLocation: Listener = (v, a) => {
		currentLocation = v;
		currentAction = a;
	};

	return {
		get location() {
			return currentLocation;
		},
		get length() {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			return hasWindow() ? window.history.length : 1; // default length is 1
		},
		get action() {
			return currentAction;
		},
		...(hasWindow()
			? {
					push: (path: string | Location) =>
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						window.location.assign(typeof path === 'string' ? path : createPath(path || {})),
					replace: (path: string | Location) =>
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						window.history.replaceState(
							{},
							// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
							document.title,
							typeof path === 'string' ? path : createPath(path || {}),
						),

					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					goBack: () => window.history.back(),

					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					goForward: () => window.history.forward(),
					listen: createLegacyListener(updateExposedLocation),
					block: () => noop,
					createHref: (location: Location) => createPath(location),
					refreshRoutes: () => noop,
				}
			: methodsPlaceholders),
	};
};
